import QuantitySelectV2 from '@/components/QuantitySelectV2'
import { numberFormat } from '@/helpers/numbers'
import { DeleteOutline } from '@mui/icons-material'
import { IconButton, Stack, Typography } from '@mui/material'
import NoImageAvailable from '@public/images/no-image-available.png'

import _ from 'lodash'
import Image from 'next/image'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { calculateCartItemTotal } from '../cartHelpers'
import { CanteenCartItem } from '../types'

type Props = {
  cartItem: CanteenCartItem
  onUpdate?: (cart: CanteenCartItem) => void
  onRemove?: (itemReference: string) => void
  showQuantity?: boolean
  ordered?: boolean
  hidePrices: boolean
  maxQty: number | null
}

const CartItem = ({
  cartItem,
  onUpdate,
  onRemove = () => {},
  showQuantity,
  ordered,
  hidePrices,
  maxQty,
}: Props) => {
  const { t } = useTranslation()
  const onCartItemUpdated = (qty: number) => {
    const updatedItem = _.cloneDeep(cartItem)
    updatedItem.qty = qty
    onUpdate?.(updatedItem)
  }

  const onCartItemRemoved = () => {
    onRemove(cartItem.reference)
  }

  const getItemName = (item: { name: string }, length: number) => {
    const name = item.name
    if (length && name && name.length > length) {
      return `${name.slice(0, length)}...`
    }
    return name
  }

  const { qty, options, item } = cartItem
  const hasOptions = options?.length > 0

  const descriptionLength = showQuantity ? 40 : 55
  const itemDescription = ordered ? cartItem.name : getItemName(item, descriptionLength)

  const totalPrice = calculateCartItemTotal(cartItem)

  const imageUrl = item?.image || NoImageAvailable.src

  return (
    <Stack paddingY={1}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Image
          style={{ borderRadius: 6, objectFit: 'cover', minWidth: 64, minHeight: 64 }}
          width={64}
          height={64}
          alt={item?.name}
          src={imageUrl}
        />
        <Stack flex={1}>
          <Typography variant="body2">{itemDescription}</Typography>
          {hasOptions ? (
            <Typography variant="caption" color="text.secondary">
              {options
                .map((option) => option.name)
                .filter(Boolean)
                .join(', ')}
            </Typography>
          ) : null}
        </Stack>

        {!hidePrices ? (
          <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
            {numberFormat(totalPrice)} {t('cc:credits')}
          </Typography>
        ) : null}
      </Stack>

      {!ordered ? (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          <QuantitySelectV2
            max={maxQty}
            min={1}
            quantity={qty}
            onChange={onCartItemUpdated}
            size="small"
          />
          <IconButton
            size="small"
            color="error"
            aria-label={t('cc:remove')}
            onClick={() => onCartItemRemoved()}>
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Stack>
      ) : null}
    </Stack>
  )
}

export default CartItem
